import { Box, Flex, Heading, Text } from '@everlywell/ui-kit';
import Carousel from 'components/Carousel/Carousel';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import ShapeCard, { ShapeCardProps } from 'components/ShapeCard/ShapeCard';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

export type ShapeCardsProps = {
  eyebrow?: string | null;
  title?: string | null;
  shapeCardsData?: ShapeCardProps[] | null;
  disclaimer?: string | null;
};

/**
 * A collection of ShapeCard components.
 * @param {ShapeCardsProps} props
 * @returns {JSX.Element}
 */
const ShapeCards = ({
  eyebrow,
  title,
  shapeCardsData,
}: ShapeCardsProps): JSX.Element => {
  const trackClick = (cardIndex: number, label?: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'ShapeCards',
        ctaText: label,
        cardIndex: cardIndex + 1,
      },
    });
  };

  return (
    <SectionContainer size={['sm', null, 'xl']}>
      {eyebrow ? (
        <Text
          marginX="auto"
          size="lg"
          color="viridian.base"
          textAlign="center"
          marginBottom={[1, null, 2]}
        >
          {eyebrow}
        </Text>
      ) : null}

      {title ? (
        <Heading
          as="h2"
          size="2xl"
          textAlign="center"
          marginBottom={[6, null, 10]}
        >
          {title}
        </Heading>
      ) : null}

      {/* Mobile - slides */}
      <Box display={['block', null, 'none']} data-testid="mobile-shape-cards">
        {shapeCardsData ? (
          <Carousel
            spaceBetween={16}
            slidesPerView="auto"
            showPagination
            slideStyle={{
              display: 'flex',
              flexDirection: 'column',
              height: 'auto',
            }}
          >
            {shapeCardsData?.map((card, index) => (
              <ShapeCard
                key={`shape-card-${index}`}
                onCtaClick={(label) => trackClick(index, label)}
                {...card}
              />
            ))}
          </Carousel>
        ) : null}
      </Box>

      {/* Desktop - cards side by side */}
      <Flex
        display={['none', null, 'flex']}
        gap="4"
        data-testid="desktop-shape-cards"
      >
        {shapeCardsData?.map((card, index) => (
          <ShapeCard
            key={`shape-card-${index}`}
            onCtaClick={(label) => trackClick(index, label)}
            {...card}
          />
        ))}
      </Flex>
    </SectionContainer>
  );
};

export default ShapeCards;
